'use client';

import { useId, useState } from 'react';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import Link from '@eliadmin/core/src/components/link';
import { CmsImage } from '@eliadmin/core/src/components/nt/ui/cms-image';
import { Heading } from '@eliadmin/core/src/components/nt/ui/heading';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { RichText } from '@eliadmin/core/src/components/nt/ui/rich-text';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { guideAdvice, guideTarget } from '@eliadmin/core/src/utils/nt/toner-guide';
import type { LinkData } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type GuideQuestion = { legend: string; options: string[] };

/** One toner type of the section „Druhy tonerov“ that the third answer recommends. */
export type GuideResult = {
  answer: string;
  /** Item name (HTML). */
  title: string;
  /** „Sprievodca - text“, or the card text when empty (HTML). */
  text: string;
  label: string;
  image: ImageType | null;
  inkImage: ImageType | null;
  href: string;
  button: string;
  /** Category per printer answer, in the order of the first question („Laserovú | Atramentovú“). */
  printerHrefs: string[];
};

/**
 * Figma 23:1224: three questions (radio groups styled as Figma „div.seg“) and the recommendation.
 * Technology selects the category, priority selects the cartridge origin and volume explains
 * which capacity to compare. Compatibility still needs the customer's exact printer model.
 */
export function GuideQuiz({
  intro,
  printer,
  volume,
  need,
  results,
  finder,
}: {
  intro: string;
  printer: GuideQuestion | null;
  volume: GuideQuestion | null;
  need: string;
  results: GuideResult[];
  finder: LinkData | null;
}) {
  const firstPrinter = printer?.options.length ? 0 : null;
  const firstVolume = volume?.options.length ? 0 : null;
  const firstResult = results.length ? 0 : null;
  const [printerIndex, setPrinterIndex] = useState<number | null>(firstPrinter);
  const [volumeIndex, setVolumeIndex] = useState<number | null>(firstVolume);
  const [resultIndex, setResultIndex] = useState<number | null>(firstResult);
  const advice = guideAdvice(printer ? printerIndex : 2, volume ? volumeIndex : 0);
  const selected = resultIndex === null ? undefined : results[resultIndex];
  const result = advice.kind === 'ready' ? selected : undefined;
  const target = result ? guideTarget(result, printerIndex ?? 0) : '';
  const label = (value: string) =>
    advice.kind === 'ready' && advice.ink ? value.replace(/tonery/gi, 'atramentové náplne') : value;
  const answered = [printerIndex, volumeIndex, resultIndex].filter(value => value !== null).length;
  const finderHref = finder?.href || '/#hladat';
  const volumeLabel = volume?.options[volumeIndex ?? 0] || '';
  const resultImage = advice.kind === 'ready' && advice.ink ? result?.inkImage : result?.image;

  return (
    <div className="grid w-full overflow-hidden rounded-2xl bg-white shadow-soft lg:grid-cols-[minmax(0,677fr)_minmax(0,651fr)]">
      <div className="flex min-w-0 flex-col gap-6 p-4 sm:p-6">
        <RichText html={intro} variant="nt-body-l" className="max-w-[652px] text-[#363D36]" />
        {printer ? (
          <Choice question={printer} value={printerIndex} onChange={setPrinterIndex} />
        ) : null}
        {volume ? <Choice question={volume} value={volumeIndex} onChange={setVolumeIndex} /> : null}
        <Choice
          question={{ legend: need, options: results.map(item => item.answer) }}
          value={resultIndex}
          onChange={setResultIndex}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-5 rounded-2xl bg-[linear-gradient(-56.17deg,#127A26_1.61%,#E5007E_117.65%)] p-4 sm:p-6 lg:min-h-[420px]">
        <div aria-live="polite" aria-atomic="true" className="flex flex-col gap-3">
          <p className="nt-body-s text-white/80">
            {result
              ? 'Odporúčanie podľa vašich odpovedí'
              : advice.kind === 'unknown'
                ? 'Pomôžeme vám s výberom'
                : `Vybrané odpovede: ${answered} z 3`}
          </p>
          <Heading
            title={
              result
                ? label(result.title)
                : advice.kind === 'unknown'
                  ? 'Najprv zistime model tlačiarne'
                  : 'Vyberme náplň pre vás'
            }
            size="h3"
            className="text-white"
          />
          <RichText
            html={
              result
                ? result.text
                : advice.kind === 'unknown'
                  ? 'Ak neviete, či máte laserovú alebo atramentovú tlačiareň, nájdite jej model na prednom paneli alebo na štítku. Podľa neho vyhľadáme vhodné náplne.'
                  : 'Vyberte typ tlačiarne, koľko tlačíte a čo je pre vás dôležité. Ukážeme odporúčaný druh náplne aj radu k jej kapacite.'
            }
            variant="nt-body-l"
            tone="inverse"
            className="max-w-[424px] text-white"
          />
          {result && advice.kind === 'ready' ? (
            <div className="rounded-xl bg-white/10 p-3 text-white">
              <p className="nt-body-m-bold mb-1">
                {volumeLabel}
                {volumeLabel.includes('strán') ? '' : ' strán'} mesačne
              </p>
              <p className="nt-body-m">{advice.tip}</p>
            </div>
          ) : null}
        </div>
        {result && target ? (
          <Link
            href={target}
            prefetch={false}
            className="flex min-h-24 items-center gap-3 rounded-2xl bg-[#FFFFFF1A] p-3 text-white transition-colors hover:bg-[#FFFFFF26] focus-visible:shadow-focus focus-visible:outline-none"
          >
            {resultImage ? (
              <span className="relative size-20 shrink-0 overflow-hidden rounded-xl bg-white sm:size-24">
                <CmsImage
                  image={resultImage}
                  decorative
                  sizes="96px"
                  className="object-contain p-[10.5%]"
                />
              </span>
            ) : null}
            <span className="nt-title-s min-w-0 flex-1">{label(result.label)}</span>
            <Icon name="arrow-up-right" className="shrink-0" />
          </Link>
        ) : null}
        {result || advice.kind === 'unknown' ? (
          <p className="nt-body-s text-white/90">
            {result
              ? 'Druh náplne už poznáte. Pred nákupom ešte overte presný model tlačiarne; nie každá kazeta do nej pasuje.'
              : 'Nenašli ste model? Pošlite nám fotku štítku a pomôžeme vám.'}
          </p>
        ) : null}
        <div className="mt-auto flex flex-wrap gap-2">
          {result?.button && target ? (
            <Button
              asChild
              variant="dark"
              className="h-auto min-h-12 flex-[1000_1_200px] whitespace-normal py-3 text-center"
            >
              <Link href={target}>{label(result.button)}</Link>
            </Button>
          ) : null}
          {advice.kind === 'unknown' ? (
            <Button asChild variant="dark" iconLeft="camera" className="flex-[1000_1_200px]">
              <Link href="/potrebujem-pomoc">Poslať fotku štítku</Link>
            </Button>
          ) : null}
          {result || advice.kind === 'unknown' ? (
            <Button
              asChild
              variant="ghost"
              iconLeft="magnifying-glass"
              className="flex-[1_1_auto] text-white hover:bg-[#FFFFFF1A] focus-visible:bg-transparent active:bg-[#FFFFFF33]"
            >
              <Link href={finderHref}>{finder?.label || 'Nájsť podľa modelu'}</Link>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Choice({
  question,
  value,
  onChange,
}: {
  question: GuideQuestion;
  value: number | null;
  onChange: (index: number) => void;
}) {
  const name = useId();

  return (
    <fieldset className="min-w-0">
      <legend className="nt-h6 mb-2 text-[#0D110D]">{question.legend}</legend>
      <div className="flex flex-wrap gap-2">
        {question.options.map((option, index) => (
          <label key={index} className="relative cursor-pointer">
            <input
              type="radio"
              name={name}
              checked={index === value}
              onChange={() => onChange(index)}
              className="peer sr-only"
            />
            <span
              className={cn(
                'nt-button-m flex min-h-12 items-center rounded-xl border px-4 py-2 transition-colors',
                'peer-focus-visible:shadow-focus',
                index === value
                  ? 'border-[#0D110D] bg-[#0D110D] text-white'
                  : 'border-[#E2E5DF] bg-white text-[#0D110D] hover:border-[#CDD2CA] hover:bg-[#F5F6F3]',
              )}
            >
              {option}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
